import { CheckCircle2, XCircle, Smartphone, Lock, Clock, Globe, Sparkles, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * Reichhaltiges Score-Breakdown-Panel für Vertrieb.
 * Erwartet: lead.score, lead.score_category, lead.score_breakdown, lead._website_check
 */

const TONE_STYLES = {
  warn: "border-red-300 bg-red-50 text-red-900 dark:border-red-900 dark:bg-red-950/30 dark:text-red-100",
  info: "border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-100",
  good: "border-emerald-300 bg-emerald-50 text-emerald-900 dark:border-emerald-900 dark:bg-emerald-950/30 dark:text-emerald-100",
};

function ScoreBar({ label, points, max, icon, note, details }) {
  const pct = max > 0 ? Math.round((points / max) * 100) : 0;
  const barColor = pct >= 70 ? "bg-emerald-500" : pct >= 30 ? "bg-amber-500" : "bg-red-500";

  return (
    <div className="bg-card border border-border rounded-sm p-3 space-y-2" data-testid={`score-cat-${label.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "")}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-base shrink-0">{icon}</span>
          <span className="text-xs font-bold uppercase tracking-wide truncate">{label}</span>
        </div>
        <span className="font-mono font-black tabular-nums text-sm shrink-0">
          {pct}<span className="text-muted-foreground font-normal text-xs">%</span>
        </span>
      </div>
      <div className="h-1.5 bg-muted rounded-full overflow-hidden">
        <div className={`h-full ${barColor} transition-all duration-500`} style={{ width: `${pct}%` }} />
      </div>
      {note && (
        <p className="text-[11px] text-muted-foreground italic">{note}</p>
      )}
      {details && details.length > 0 && (
        <ul className="space-y-1 mt-1.5">
          {details.map((d, i) => (
            <li key={i} className="flex items-center gap-2 text-[11px]">
              {d.present ? (
                <CheckCircle2 size={11} className="text-emerald-600 shrink-0" />
              ) : (
                <XCircle size={11} className="text-muted-foreground/50 shrink-0" />
              )}
              <span className={`truncate ${d.present ? "text-foreground" : "text-muted-foreground line-through"}`}>{d.label}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function PitchHooks({ hooks }) {
  if (!hooks || hooks.length === 0) return null;
  return (
    <div className="space-y-1.5" data-testid="pitch-hooks">
      <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold flex items-center gap-1.5">
        <Sparkles size={10} /> Pitch-Hooks für den Anruf
      </p>
      {hooks.map((h, i) => (
        <div
          key={i}
          className={`flex gap-2 p-2 rounded-sm border text-[11px] leading-snug ${TONE_STYLES[h.tone] || TONE_STYLES.info}`}
          data-testid={`pitch-hook-${i}`}
        >
          <span className="text-base shrink-0 leading-none mt-0.5">{h.icon}</span>
          <span>{h.text}</span>
        </div>
      ))}
    </div>
  );
}

// Friendly Erklärungen für Tech-Signale (Beweise im Source-Code der Website)
const SIGNAL_LABELS = {
  // Pre-2014 / very_old
  "kein-viewport-meta": { label: "Kein Viewport-Meta", hint: "Pre-2013 – nicht mobil-optimiert" },
  "xhtml-doctype": { label: "XHTML-DOCTYPE", hint: "Pre-2014 Markup-Standard" },
  "xhtml-namespace": { label: "XHTML-Namespace", hint: "<html xmlns=… > ist Pre-2014" },
  "html4-doctype": { label: "HTML4-DOCTYPE", hint: "Pre-2010 Markup" },
  "font-tag": { label: "<font>-Tag", hint: "90er-Jahre HTML" },
  "center-tag": { label: "<center>-Tag", hint: "90er-Jahre HTML" },
  "frameset": { label: "Frames", hint: "<frameset> – 90er-Jahre" },
  "marquee/blink": { label: "Marquee/Blink", hint: "90er-Jahre Erbe" },
  "ms-office-export": { label: "MS-Office-Export", hint: "Word→HTML, oft 2000er" },
  "legacy-http-equiv": { label: "Legacy http-equiv", hint: "Expires/Pragma – alte Schule" },
  "id-layout-pre-framework": { label: "ID-Layout (head/nav/content)", hint: "Pre-Bootstrap-Aufbau" },
  "fixed-width-body": { label: "Fixed-Width-Body", hint: "Festbreite, kein Responsive" },
  "ie-conditional-comments": { label: "IE-Conditional-Comments", hint: "Pre-2014, IE-Support" },
  "ie6-7-hacks": { label: "IE6/7-CSS-Hacks", hint: "Pre-2014" },
  "bootstrap-1-or-2": { label: "Bootstrap 1.x/2.x", hint: "Pre-2014 Framework-Version" },
  "alte-jquery-plugins": { label: "Alte jQuery-Plugins", hint: "prettyPhoto, fancybox 1.x, …" },
  "legacy-slider-lib": { label: "Legacy-Slider", hint: "nivo-slider, jcarousel, …" },
  "flash-embed": { label: "Flash-Embed", hint: "Vor-2017 Tech, tot" },
  "legacy-fonts-only": { label: "Legacy-Fonts (Comic-Sans/Verdana)", hint: "Keine moderne Web-Font" },
  "ionos-mywebsite": { label: "IONOS MyWebsite", hint: "Veralteter Baukasten" },
  "typo3-classic": { label: "TYPO3 (alt)", hint: "Veraltetes CMS" },
  "joomla-classic": { label: "Joomla 1.x/2.x", hint: "Veraltetes CMS" },
  "drupal-old": { label: "Drupal ≤7", hint: "Veraltetes CMS" },
  "wp-old-theme": { label: "WordPress (altes Theme)", hint: "Pre-2014 Theme-Marker" },
  // Modern signals
  "viewport-meta": { label: "Viewport-Meta", hint: "Mobile-fähig", tone: "good" },
  "open-graph": { label: "Open-Graph-Tags", hint: "Modern (Social-Sharing)", tone: "good" },
  "schema-org": { label: "Schema.org JSON-LD", hint: "Strukturierte Daten", tone: "good" },
  "css-grid/flex": { label: "CSS Grid/Flex", hint: "Modernes Layout", tone: "good" },
  "modern-framework": { label: "Modernes Framework", hint: "React/Vue/Next/Webflow", tone: "good" },
};

function _label(sig) {
  // Erkennt auch parametrisierte Signale wie "shy-entities(15×)" oder "tabellen-layout(3×)"
  const base = sig.replace(/\(\d+×?\)$/, "").trim();
  if (SIGNAL_LABELS[base]) return { ...SIGNAL_LABELS[base], raw: sig };
  if (sig.startsWith("jquery-")) return { label: sig, hint: "Veraltete jQuery-Major-Version", raw: sig };
  if (sig.startsWith("inline-styles")) return { label: sig, hint: "Viele Inline-Styles ohne Framework", raw: sig };
  if (sig.startsWith("baukasten-inline-styles")) return { label: "Baukasten Inline-Styles", hint: "Wix/Jimdo-V1-Pattern", raw: sig };
  if (sig.startsWith("shy-entities")) return { label: "Soft-Hyphens (&shy;)", hint: "Vor CSS hyphens:auto üblich", raw: sig };
  if (sig.startsWith("tabellen-layout")) return { label: "Tabellen-Layout", hint: "Hard-Indikator 2000er-Web", raw: sig };
  if (sig.startsWith("float-layout")) return { label: "Float-Layout", hint: "Pre-Flex/Grid", raw: sig };
  if (sig.startsWith("jquery-ui-")) return { label: sig, hint: "Pre-2013 jQuery-UI", raw: sig };
  return { label: sig, hint: null, raw: sig };
}

function TechSignalsDetails({ check }) {
  const old = check?.tech_signals_old || [];
  const modern = check?.tech_signals_modern || [];
  if (old.length === 0 && modern.length === 0) return null;
  const verdict = check?.tech_verdict;
  const verdictLabel = verdict === "very_old" ? "Sehr alt" : verdict === "old" ? "Veraltet" : verdict === "modern" ? "Modern" : "Unklar";

  return (
    <details className="group bg-card/60 border border-border rounded-sm" data-testid="tech-signals-details">
      <summary className="text-[10px] cursor-pointer select-none px-2 py-1.5 flex items-center justify-between gap-2 list-none [&::-webkit-details-marker]:hidden">
        <span className="font-semibold text-muted-foreground tracking-wide uppercase">
          Beweise: {old.length} alt · {modern.length} modern
        </span>
        <span className="text-[9px] text-muted-foreground/70 group-open:rotate-180 transition-transform">▼</span>
      </summary>
      <div className="px-2 pb-2 pt-1 space-y-2">
        {old.length > 0 && (
          <div>
            <p className="text-[9px] uppercase tracking-wider text-red-600 dark:text-red-400 font-bold mb-1">
              Pre-2014 Indikatoren ({verdictLabel})
            </p>
            <div className="flex flex-wrap gap-1">
              {old.map((s) => {
                const m = _label(s);
                return (
                  <span
                    key={s}
                    title={m.hint || s}
                    className="inline-flex items-center text-[10px] px-1.5 py-0.5 rounded-sm border border-red-300 bg-red-50 text-red-900 dark:border-red-900 dark:bg-red-950/40 dark:text-red-200 font-mono"
                    data-testid={`tech-signal-old-${m.raw}`}
                  >
                    {m.label}
                  </span>
                );
              })}
            </div>
          </div>
        )}
        {modern.length > 0 && (
          <div>
            <p className="text-[9px] uppercase tracking-wider text-emerald-600 dark:text-emerald-400 font-bold mb-1">
              Moderne Indikatoren
            </p>
            <div className="flex flex-wrap gap-1">
              {modern.map((s) => {
                const m = _label(s);
                return (
                  <span
                    key={s}
                    title={m.hint || s}
                    className="inline-flex items-center text-[10px] px-1.5 py-0.5 rounded-sm border border-emerald-300 bg-emerald-50 text-emerald-900 dark:border-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-200 font-mono"
                    data-testid={`tech-signal-modern-${m.raw}`}
                  >
                    {m.label}
                  </span>
                );
              })}
            </div>
          </div>
        )}
        <p className="text-[9px] text-muted-foreground italic">
          Quelle: HTML-/CSS-Analyse der Website (`lead_scoring._detect_tech_age`). Hover über ein Tag für Details.
        </p>
      </div>
    </details>
  );
}



function WebsiteTech({ check }) {
  if (!check) return null;
  const checkedAt = check.checked_at ? new Date(check.checked_at) : null;
  const checkedAgo = checkedAt ? Math.round((Date.now() - checkedAt.getTime()) / 86400000) : null;

  // Alter immer direkt aus dem Cache lesen (single source of truth)
  const ageSeconds = check.age_seconds;
  const yearsOld = ageSeconds != null ? ageSeconds / (365.25 * 86400) : null;

  const ageItem = (() => {
    if (yearsOld == null) return { value: "unbekannt", sub: "Kein Alters-Signal", tone: "neutral" };
    if (yearsOld < 1) return { value: `${Math.round(yearsOld * 12)} Mon.`, sub: "Frisch", tone: "good" };
    if (yearsOld < 2) return { value: `${yearsOld.toFixed(1)} Jahre`, sub: "Aktuell", tone: "good" };
    if (yearsOld < 3) return { value: `${yearsOld.toFixed(1)} Jahre`, sub: "Veraltet", tone: "info" };
    if (yearsOld < 5) return { value: `${yearsOld.toFixed(1)} Jahre`, sub: "Sehr alt", tone: "warn" };
    return { value: `${Math.round(yearsOld)} Jahre`, sub: "Modernisierung dringend", tone: "warn" };
  })();

  const items = [
    { icon: Clock, label: "Alter", value: ageItem.value, sub: ageItem.sub, tone: ageItem.tone },
    {
      icon: Smartphone, label: "Mobile",
      value: check.is_mobile_friendly ? "Ja" : "Nein",
      sub: check.is_mobile_friendly ? "Viewport-Meta ok" : "Kein Viewport",
      tone: check.is_mobile_friendly ? "good" : "warn",
    },
    {
      icon: Lock, label: "Verbindung",
      value: check.has_ssl ? "HTTPS" : "HTTP",
      sub: check.has_ssl ? "TLS aktiv" : "Unverschlüsselt",
      tone: check.has_ssl ? "good" : "warn",
    },
    {
      icon: Globe, label: "Tech",
      value: check.tech_verdict === "very_old" ? "Sehr alt"
           : check.tech_verdict === "old" ? "Veraltet"
           : check.tech_verdict === "modern" ? "Modern"
           : check.used_playwright ? "JS-Site" : "Statisch",
      sub: check.tech_verdict === "very_old" ? "HTML4/Tabellen-Layout"
         : check.tech_verdict === "old" ? "Pre-2014 Indikatoren"
         : check.tech_verdict === "modern" ? "Aktuelle Frameworks"
         : check.used_playwright ? "Wix/Jimdo/SPA" : "Klassisches HTML",
      tone: check.tech_verdict === "very_old" ? "warn"
          : check.tech_verdict === "old" ? "warn"
          : check.tech_verdict === "modern" ? "good"
          : check.used_playwright ? "info" : "neutral",
    },
  ];

  const cellTone = (t) => ({
    warn: "border-red-300 bg-red-50/60 dark:border-red-900 dark:bg-red-950/30",
    info: "border-amber-300 bg-amber-50/60 dark:border-amber-900 dark:bg-amber-950/30",
    good: "border-emerald-300 bg-emerald-50/60 dark:border-emerald-900 dark:bg-emerald-950/30",
    neutral: "border-border bg-muted/20",
  }[t] || "border-border bg-muted/20");

  const valueTone = (t) => ({
    warn: "text-red-700 dark:text-red-300",
    info: "text-amber-700 dark:text-amber-300",
    good: "text-emerald-700 dark:text-emerald-300",
    neutral: "text-foreground",
  }[t] || "text-foreground");

  return (
    <div className="space-y-2" data-testid="website-tech">
      <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold flex items-center gap-1.5">
        <Globe size={10} /> Website-Tech-Status
      </p>
      <div className="grid grid-cols-2 gap-1.5">
        {items.map((it, i) => {
          const Icon = it.icon;
          return (
            <div
              key={i}
              className={`flex items-center gap-2 p-2 rounded-sm border ${cellTone(it.tone)}`}
              data-testid={`tech-${it.label.toLowerCase()}`}
            >
              <Icon size={14} className="text-muted-foreground shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="text-[9px] uppercase tracking-wider text-muted-foreground font-bold leading-none mb-0.5">
                  {it.label}
                </p>
                <p className={`text-xs font-bold leading-tight truncate ${valueTone(it.tone)}`}>
                  {it.value}
                </p>
                <p className="text-[9px] text-muted-foreground/80 leading-none mt-0.5 truncate">
                  {it.sub}
                </p>
              </div>
            </div>
          );
        })}
      </div>
      <TechSignalsDetails check={check} />
      {checkedAgo !== null && (
        <p className="text-[10px] text-muted-foreground italic">
          Letzte Prüfung: {checkedAgo === 0 ? "heute" : checkedAgo === 1 ? "gestern" : `vor ${checkedAgo} Tagen`}
          {check.copyright_year ? ` · Copyright-Jahr im HTML: ${check.copyright_year}` : ""}
        </p>
      )}
    </div>
  );
}

export default function ScoreBreakdownPanel({ lead, onRefresh }) {
  if (lead?.score == null) return null;
  const bd = lead.score_breakdown || {};
  const cat = lead.score_category;

  const categoryLabel = cat === "hot" ? "🔥 Heißer Lead" : cat === "warm" ? "🌡 Warmer Lead" : "❄ Kalter Lead";
  const categoryColor =
    cat === "hot" ? "text-red-600 dark:text-red-400" :
    cat === "warm" ? "text-amber-600 dark:text-amber-400" : "text-slate-500";

  return (
    <div className="bg-muted/30 border border-border rounded-sm p-3 space-y-3" data-testid="score-box">
      {/* Header */}
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs uppercase tracking-widest text-muted-foreground font-bold">Lead-Score (Vertrieb)</p>
        {onRefresh && (
          <Button
            size="sm" variant="ghost"
            className="h-6 text-[10px] gap-1"
            onClick={onRefresh}
            data-testid="refresh-score-btn"
          >
            <RefreshCw size={10} /> Neu prüfen
          </Button>
        )}
      </div>

      {/* Big Score */}
      <div className="flex items-baseline gap-2">
        <span className="text-4xl font-black tracking-tight font-mono tabular-nums">{lead.score}</span>
        <span className="text-xs text-muted-foreground">/ 100</span>
        <span className={`text-sm font-bold uppercase tracking-wider ml-1 ${categoryColor}`}>
          {categoryLabel}
        </span>
      </div>

      {/* 2 Score-Bars (Aktivität fließt nicht in Score ein) */}
      <div className="space-y-2">
        <ScoreBar
          label="Website-Bedarf"
          icon="🌐"
          points={bd.website_analysis ?? bd.website_need ?? 0}
          max={bd.website_max ?? 60}
          note={bd.website_note}
        />
        <ScoreBar
          label="Erreichbarkeit"
          icon="📞"
          points={bd.contact_quality ?? bd.data_quality ?? 0}
          max={bd.contact_max ?? 23}
          details={bd.contact_details}
        />
      </div>

      {/* Pitch-Hooks */}
      <PitchHooks hooks={bd.pitch_hooks} />

      {/* Website-Tech */}
      <WebsiteTech check={lead._website_check} />
    </div>
  );
}
